import { useMemo } from "react"
import { Empty, Spin } from "antd"
import { CalendarOutlined, UserOutlined } from "@ant-design/icons"
import moment from "moment"
import { safeMoment } from "../../../utils/safeDate.js"
import Paginator from "./table/Paginator"
import { useTranslation } from 'react-i18next';

const getRequesterName = (record = {}) => {
    if (typeof record.createdBy === "string") return record.createdBy
    return record.createdBy?.name || "-"
}

const buildBoardData = (documents = [], statuses = []) => {
    const grouped = statuses.reduce((acc, status) => {
        acc[status.status] = []
        return acc
    }, {})

    documents.forEach((doc) => {
        const statusKey = doc?.status || "draft"
        if (!grouped[statusKey]) grouped[statusKey] = []
        grouped[statusKey].push(doc)
    })

    return grouped
}

const KanbanCard = ({ card, onClick }) => {
    const { t } = useTranslation();
    const requester = getRequesterName(card)
    const date = safeMoment(card.createdOn).isValid() ? safeMoment(card.createdOn).format("DD MMM YYYY, hh:mm a") : "-"

    return (
        <div
            onClick={() => onClick(card)}
            className="group bg-white rounded-lg border border-gray-200 p-3.5 cursor-pointer hover:shadow-[0_2px_12px_rgba(0,0,0,0.10)] hover:border-gray-300 transition-all duration-150"
        >
            {/* Case ID */}
            <div className="flex items-start justify-between gap-2 mb-3">
                <span className="text-[13px] font-semibold text-gray-800 leading-snug">
                    {card.caseId || card.requestId}
                </span>
            </div>

            {/* Meta rows */}
            <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-semibold tracking-wider text-gray-400 uppercase w-20 flex-shrink-0">
                        {t('Requested By')}
                    </span>
                    <span className="text-[12px] text-gray-700 font-medium truncate">{requester}</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-semibold tracking-wider text-gray-400 uppercase w-20 flex-shrink-0">
                        {t('Created On')}
                    </span>
                    <span className="text-[12px] text-gray-600 truncate">{date}</span>
                </div>
            </div>
        </div>
    )
}

const KanbanColumn = ({ status, cards, onCardClick }) => {
    const { t } = useTranslation();
    const accent = status.color || "#eb2f96"

    return (
        <div
            className="flex-none w-[280px] flex flex-col bg-[#f5f6f8] rounded-xl border border-gray-200 overflow-hidden"
            style={{ height: "calc(100vh - 220px)" }}
        >
            {/* Header — sticky, never scrolls */}
            <div
                className="flex items-center justify-between px-4 py-3 bg-white border-b-2 flex-shrink-0"
                style={{ borderBottomColor: accent }}
            >
                <div className="flex items-center gap-2 min-w-0">
                    <span
                        className="text-[13px] font-bold truncate"
                        style={{ color: accent }}
                        title={status.label}
                    >
                        {status.label}
                    </span>
                    <span className="text-[12px] font-semibold text-gray-400 flex-shrink-0">
                        {cards.length}
                    </span>
                </div>
            </div>

            {/* Scrollable cards body */}
            <div className="flex flex-col gap-2 p-2.5 overflow-y-auto flex-1">
                {cards.length > 0 ? (
                    cards.map((card) => (
                        <KanbanCard
                            key={card.requestId}
                            card={card}
                            onClick={onCardClick}
                        />
                    ))
                ) : (
                    <div className="flex-1 flex items-center justify-center min-h-[160px]">
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={<span className="text-xs text-gray-400">{t('No requests')}</span>}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}

const KanbanBoard = ({
    documents = [],
    statuses = [],
    loading = false,
    onCardClick,
    currentPage,
    pageSize,
    total,
    onPageChange,
}) => {
    const { t } = useTranslation();
    const boardData = useMemo(() => buildBoardData(documents, statuses), [documents, statuses])

    const from = documents.length > 0 ? (currentPage - 1) * pageSize + 1 : 0
    const to = Math.min(currentPage * pageSize, total)

    return (
        <div className="flex flex-col gap-4">
            <Spin spinning={loading}>
                <div className="flex gap-3 overflow-x-auto pb-2">
                    {statuses.map((status) => (
                        <KanbanColumn
                            key={status.status}
                            status={status}
                            cards={boardData[status.status] || []}
                            onCardClick={onCardClick}
                        />
                    ))}
                </div>
            </Spin>

            <div className="flex items-center justify-between flex-wrap gap-4">
                <span className="text-[13px] text-gray-500">
                    {t('Showing')} {from} to {to} of {total} requests
                </span>
                <Paginator
                    total={total}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    onChange={onPageChange}
                />
            </div>
        </div>
    )
}

export default KanbanBoard
